export const SOUND_PROFILES = Object.freeze({
  roar: { id: 'roar', letter: 'A', level: .4732, trim: .86, bass: 4, presence: -1, cutoff: 6700, room: .12, wind: .018, whine: 0, pop: .11 },
  apex: { id: 'apex', letter: 'B', level: .6273, trim: .79, bass: 0, presence: 3, cutoff: 10500, room: .10, wind: .017, whine: .005, pop: .065 },
  raw: { id: 'raw', letter: 'C', level: .5389, trim: .83, bass: 1, presence: 1.5, cutoff: 8400, room: .065, wind: .02, whine: .009, pop: .095 },
  cockpit: { id: 'cockpit', letter: 'D', level: .6397, trim: .85, bass: 3, presence: -2, cutoff: 4700, room: .025, wind: .007, whine: .035, pop: .045 },
});

export function soundFromSearch(search) {
  const id = new URLSearchParams(search).get('sound');
  return Object.hasOwn(SOUND_PROFILES, id) ? id : null;
}

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const smooth = value => value * value * (3 - 2 * value);
const gearSpeeds = [72, 112, 155, 200, 245, 295];
// Throttle-load lag: lifting off settles over roughly half a second.
const LOAD_TAU = .16;

// This drivetrain controls sound only. It never changes the race simulation.
export class EngineDrive {
  constructor() { this.reset(); }

  reset() {
    this.gear = 1;
    this.cooldown = 0;
    this.cut = 0;
    this.blip = 0;
    this.load = .25;
    this.lastStage = '';
  }

  update({ stage, elapsed = 0, speed = 0, acceleration = 0 }, delta = 1 / 60) {
    const dt = clamp(Number.isFinite(delta) ? delta : 0, 0, .25);
    const kph = clamp(Number.isFinite(speed) ? speed * 3.6 : 0, 0, 320);
    const deceleration = Number.isFinite(acceleration) ? acceleration : 0;
    const running = stage === 'racing' || stage === 'celebrating';
    const preparing = stage === 'grid' || stage === 'countdown';
    if (!running) this.reset();
    if (running && !['racing', 'celebrating'].includes(this.lastStage)) {
      this.gear = Math.min(6, Math.max(1, gearSpeeds.findIndex(value => kph / value * 7800 < 7400) + 1));
      if (kph > 280) this.gear = 6;
    }
    this.lastStage = stage;
    this.cooldown = Math.max(0, this.cooldown - dt);
    this.cut = Math.max(0, this.cut - dt);
    this.blip = Math.max(0, this.blip - dt);
    let shift = null;
    let rpm = kph / gearSpeeds[this.gear - 1] * 7800;
    if (running && this.cooldown <= 0) {
      if (rpm > 7450 && this.gear < 6 && deceleration > -1.5) {
        this.gear++; this.cut = .12; shift = 'up'; this.cooldown = .48;
      } else if (rpm < 4150 && this.gear > 1 && deceleration < -1.5) {
        this.gear--; this.blip = .20; shift = 'down'; this.cooldown = .48;
      }
      rpm = kph / gearSpeeds[this.gear - 1] * 7800;
    }
    const driveLoad = running ? clamp(.67 + deceleration * .09, .09, 1) : .25;
    this.load += (driveLoad - this.load) * (1 - Math.exp(-dt / LOAD_TAU));
    let load = running ? this.load : driveLoad;
    if (this.cut > 0) load = .12;
    if (this.blip > 0) { rpm *= 1 + .10 * this.blip / .2; load = .65; }
    if (running && kph < 38 && stage === 'racing') rpm = Math.max(rpm, 3800 + kph * 30);
    if (preparing) {
      const t = Math.max(0, elapsed);
      const rev = Math.exp(-(((t - 1.25) / .34) ** 2)) + .85 * Math.exp(-(((t - 2.75) / .4) ** 2));
      rpm = 1400 + 3150 * rev + 3500 * smooth(clamp((t - 3.5) / 1.3, 0, 1));
      load = .22 + .4 * clamp((rpm - 1400) / 3400, 0, 1);
      this.load = load;
    }
    return { active: running || preparing, rpm: clamp(rpm, 1250, 8300), gear: this.gear, load,
      speed: kph / 3.6, shift, blip: this.blip > 0, braking: running && deceleration < -2 };
  }
}

export const AUDITION_DURATION = 26;
const auditionKeys = [[0, 0], [2, 0], [3, 0], [7, 34], [11, 63], [14, 27], [16, 27], [21.5, 72], [23.2, 72], [26, 25]];

export function auditionFrame(elapsed) {
  const t = Math.max(0, elapsed);
  if (t >= AUDITION_DURATION) return { stage: 'result', elapsed: t, speed: 0, acceleration: 0, segment: 'done' };
  let i = 1;
  while (i < auditionKeys.length - 1 && t > auditionKeys[i][0]) i++;
  const [from, a] = auditionKeys[i - 1], [to, b] = auditionKeys[i];
  const speed = a + (b - a) * (t - from) / (to - from);
  const segment = t < 3 ? 'grid' : t < 11 ? 'launch' : t < 14 ? 'brake' : t < 16 ? 'corner' : t < 23.2 ? 'exit' : 'coast';
  return { stage: t < 3 ? 'countdown' : 'racing', elapsed: t < 3 ? t * 5 / 3 : t - 3,
    speed, acceleration: (b - a) / (to - from), segment };
}

function noiseBuffer(context, seconds, seed) {
  const buffer = context.createBuffer(2, Math.ceil(context.sampleRate * seconds), context.sampleRate);
  for (let channel = 0; channel < 2; channel++) {
    const data = buffer.getChannelData(channel);
    let state = (seed + channel * 1823) >>> 0, pink = 0;
    for (let i = 0; i < data.length; i++) {
      state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
      const white = state / 2147483648 - 1;
      pink = pink * .965 + white * .15;
      data[i] = pink + white * .08;
    }
    const seam = Math.floor(context.sampleRate * .05);
    for (let i = 0; i < seam; i++) data[i] = data[i] * i / seam + data[data.length - seam + i] * (1 - i / seam);
  }
  return buffer;
}

export class SampledEngine {
  static async load(context, id) {
    if (!Object.hasOwn(SOUND_PROFILES, id)) throw new RangeError('Unknown sound profile.');
    return Promise.all([0, 1, 2].map(async index => {
      const response = await fetch(`assets/audio/${id}-${index}.wav`);
      if (!response.ok) throw new Error('Engine samples could not be loaded.');
      return context.decodeAudioData(await response.arrayBuffer());
    }));
  }

  constructor(context, destination, id, buffers, startTime = context.currentTime) {
    this.context = context;
    this.profile = SOUND_PROFILES[id];
    this.drive = new EngineDrive();
    this.lastTime = null;
    this.lastBrake = false;
    this.disposed = false;
    this.nodes = [];
    this.sources = [];
    const gain = value => { const node = context.createGain(); node.gain.value = value; this.nodes.push(node); return node; };
    const filter = (type, frequency, value = 0) => {
      const node = context.createBiquadFilter(); node.type = type; node.frequency.value = frequency;
      node.gain.value = value; node.Q.value = .6; this.nodes.push(node); return node;
    };
    this.output = gain(this.profile.level);
    this.limiter = context.createDynamicsCompressor();
    this.limiter.threshold.value = -8; this.limiter.knee.value = 5; this.limiter.ratio.value = 10;
    this.limiter.attack.value = .004; this.limiter.release.value = .12;
    this.nodes.push(this.limiter);
    this.limiter.connect(this.output); this.output.connect(destination);
    this.loadGain = gain(0);
    this.tone = filter('lowpass', 4000);
    const bass = filter('lowshelf', 240, this.profile.bass);
    const presence = filter('peaking', 1600, this.profile.presence);
    const highpass = filter('highpass', 42);
    this.loadGain.connect(highpass); highpass.connect(bass); bass.connect(presence);
    presence.connect(this.tone); this.tone.connect(this.limiter);
    const room = context.createDelay(.2); room.delayTime.value = .047;
    const roomGain = gain(this.profile.room); const roomFilter = filter('lowpass', 2100);
    this.nodes.push(room);
    this.tone.connect(room); room.connect(roomFilter); roomFilter.connect(roomGain); roomGain.connect(this.limiter);
    this.bands = buffers.map((buffer, index) => {
      const source = context.createBufferSource(); source.buffer = buffer; source.loop = true;
      const envelope = gain(0); source.connect(envelope); envelope.connect(this.loadGain);
      source.start(startTime, index * .137 % buffer.duration); this.sources.push(source);
      return { source, envelope, rpm: [2300, 4500, 7200][index] };
    });
    const wind = context.createBufferSource(); wind.buffer = noiseBuffer(context, 2.3, 719); wind.loop = true;
    const windFilter = filter('bandpass', 850); windFilter.Q.value = .4;
    this.windGain = gain(0); wind.connect(windFilter); windFilter.connect(this.windGain); this.windGain.connect(this.limiter);
    wind.start(startTime); this.sources.push(wind);
    this.whine = context.createOscillator(); this.whine.type = 'sine';
    this.whineGain = gain(0); this.whine.connect(this.whineGain); this.whineGain.connect(this.limiter);
    this.whine.start(startTime); this.sources.push(this.whine);
    this.popBuffer = noiseBuffer(context, .13, 1887);
  }

  transient(time, amount, shift) {
    const context = this.context;
    const source = context.createBufferSource(); source.buffer = this.popBuffer;
    const envelope = context.createGain();
    const tone = context.createBiquadFilter(); tone.type = 'lowpass'; tone.frequency.value = shift ? 1900 : 950;
    envelope.gain.setValueAtTime(0, time);
    envelope.gain.linearRampToValueAtTime(amount, time + .003);
    envelope.gain.exponentialRampToValueAtTime(.0001, time + (shift ? .055 : .12));
    source.connect(tone); tone.connect(envelope); envelope.connect(this.limiter);
    source.onended = () => { source.disconnect(); tone.disconnect(); envelope.disconnect(); this.sources = this.sources.filter(item => item !== source); };
    this.sources.push(source); source.start(time); source.stop(time + .13);
  }

  update(frame, time = this.context.currentTime) {
    if (this.disposed) return null;
    const dt = this.lastTime === null ? 1 / 60 : clamp(time - this.lastTime, 0, .25);
    this.lastTime = time;
    const state = this.drive.update(frame, dt);
    const set = (parameter, value, smoothTime = .035) => parameter.setTargetAtTime(value, time, smoothTime);
    let blend = clamp((state.rpm - 2300) / 2200, 0, 1);
    let weights = [Math.cos(blend * Math.PI / 2), Math.sin(blend * Math.PI / 2), 0];
    if (state.rpm > 4500) {
      blend = clamp((state.rpm - 4500) / 2700, 0, 1);
      weights = [0, Math.cos(blend * Math.PI / 2), Math.sin(blend * Math.PI / 2)];
    }
    for (const [i, band] of this.bands.entries()) {
      set(band.source.playbackRate, clamp(state.rpm / band.rpm, .48, 1.9), state.shift ? .018 : .04);
      set(band.envelope.gain, weights[i]);
    }
    set(this.loadGain.gain, state.active ? (.27 + .66 * state.load) * this.profile.trim : 0, state.shift ? .012 : .045);
    set(this.tone.frequency, 1800 + this.profile.cutoff * (.22 + .78 * state.load));
    set(this.windGain.gain, state.active ? (state.speed / 75) ** 1.6 * this.profile.wind : 0, .12);
    set(this.whine.frequency, 550 + state.rpm * .115);
    set(this.whineGain.gain, state.active ? this.profile.whine * state.load * clamp(state.rpm / 6000, 0, 1) : 0);
    if (state.shift) this.transient(time, this.profile.pop * (state.shift === 'up' ? 1.1 : .6), true);
    if (state.braking && !this.lastBrake) {
      this.transient(time + .06, this.profile.pop, false);
      this.transient(time + .19, this.profile.pop * .55, false);
    }
    this.lastBrake = state.braking;
    return state;
  }

  setVolume(value, time = this.context.currentTime) {
    this.output.gain.setTargetAtTime(clamp(value, 0, 1) * this.profile.level, time, .03);
  }

  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    for (const source of this.sources) { try { source.stop(); } catch {} source.disconnect(); }
    for (const node of this.nodes) node.disconnect();
    this.sources = [];
  }
}
